import { Clock, Lock, Mail, MapPin, Phone, Shield, User } from "lucide-react";
import { type ReactNode, useState } from "react";
import { iniciales } from "../../components/layout/usuario";
import { Card, PageHeader } from "../../components/ui/patrones";
import { Aviso, Boton, Campo, Modal } from "../../components/ui/primitivos";
import { authClient } from "../../lib/auth-client";
import { actualizarPerfil } from "../../lib/consola-api";
import { OPCIONES_MINUTOS, usePreferenciaMinutos } from "../../lib/inactividad";
import { useSession } from "../../lib/SessionContext";

/**
 * Mi Perfil — porteada de `ProfilePage.tsx` de `imprenta`.
 *
 * La versión anterior mostraba tres tarjetas con **todos los formularios
 * abiertos a la vez**: los datos personales siempre editables, el cambio de
 * email y el de contraseña desplegados uno debajo del otro. Se leía como una
 * lista de trámites pendientes.
 *
 * `imprenta` la arma al revés y por eso se replica: **Información Personal en
 * modo LECTURA**, con un botón «Editar Perfil» que la vuelve formulario; y
 * **Seguridad** como dos renglones con su botón, que abren en modal. Lo que uno
 * viene a hacer acá casi siempre es *mirar* —qué teléfono tengo cargado, con qué
 * rol entré—; editar es la excepción.
 *
 * **La contraseña la maneja Better Auth**, no un endpoint nuestro:
 * `/change-password` verifica la actual antes de aceptar la nueva. Escribir el
 * nuestro sería reimplementar verificación y hashing al lado del que ya corre.
 */

export function PerfilPagina() {
  const { me, recargar } = useSession();

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Mi Perfil"
        subtitulo="Administra tu información personal y configuración de cuenta"
      />

      {me ? <InformacionPersonal me={me} alGuardar={recargar} /> : null}
      {me ? <Seguridad emailActual={me.email} /> : null}
      <ConfiguracionDeSesion />
    </div>
  );
}

/** El encabezado de una tarjeta: título, descripción y acción a la derecha. */
function CabeceraDeTarjeta({
  titulo,
  descripcion,
  accion,
}: {
  titulo: string;
  descripcion: string;
  accion?: ReactNode;
}) {
  return (
    <div className="mb-6 flex items-start justify-between gap-4">
      <div>
        <h2 className="font-semibold text-lg text-neutral-900">{titulo}</h2>
        <p className="text-neutral-500 text-sm">{descripcion}</p>
      </div>
      {accion}
    </div>
  );
}

/** Un dato en modo lectura: ícono, etiqueta arriba y valor abajo. */
function Dato({
  icono: Icono,
  etiqueta,
  valor,
}: {
  icono: typeof User;
  etiqueta: string;
  valor: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <Icono aria-hidden="true" className="h-5 w-5 shrink-0 text-neutral-400" />
      <div className="min-w-0">
        <p className="font-medium text-neutral-500 text-sm">{etiqueta}</p>
        <p className="truncate text-base text-neutral-900">{valor}</p>
      </div>
    </div>
  );
}

/** Un campo del formulario con su ícono adentro, a la izquierda. */
function CampoConIcono({
  icono: Icono,
  etiqueta,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { icono: typeof User; etiqueta: string }) {
  const id = props.id ?? etiqueta;
  return (
    <div className="space-y-2">
      <label htmlFor={id} className="block font-medium text-neutral-700 text-sm">
        {etiqueta}
      </label>
      <div className="relative">
        <Icono aria-hidden="true" className="absolute top-3 left-3 h-4 w-4 text-neutral-400" />
        <input
          id={id}
          {...props}
          className="w-full rounded-md border border-neutral-300 bg-white py-2 pr-3 pl-10 text-neutral-900 text-sm"
        />
      </div>
    </div>
  );
}

function InformacionPersonal({
  me,
  alGuardar,
}: {
  me: {
    nombre: string;
    email: string;
    telefono: string | null;
    direccion: string | null;
    roles: string[];
  };
  alGuardar: () => Promise<void>;
}) {
  const [editando, setEditando] = useState(false);
  const [datos, setDatos] = useState({
    nombre: me.nombre,
    telefono: me.telefono ?? "",
    direccion: me.direccion ?? "",
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listo, setListo] = useState(false);

  /** Cancelar descarta lo tipeado y vuelve a lo que hay guardado. */
  const cancelar = () => {
    setDatos({ nombre: me.nombre, telefono: me.telefono ?? "", direccion: me.direccion ?? "" });
    setError(null);
    setEditando(false);
  };

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setGuardando(true);
    setError(null);
    setListo(false);
    try {
      await actualizarPerfil({
        nombre: datos.nombre.trim(),
        telefono: datos.telefono.trim() || null,
        direccion: datos.direccion.trim() || null,
      });
      // Recarga la sesión: el nombre se muestra en el pie del sidebar.
      await alGuardar();
      setListo(true);
      setEditando(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar");
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Card className="p-6">
      <CabeceraDeTarjeta
        titulo="Información Personal"
        descripcion="Tu información básica de perfil"
        accion={
          editando ? undefined : (
            <Boton variante="secundario" onClick={() => setEditando(true)}>
              Editar Perfil
            </Boton>
          )
        }
      />

      {error ? <Aviso>{error}</Aviso> : null}
      {listo ? <Aviso tono="ok">Datos actualizados.</Aviso> : null}

      {/* El avatar es de solo lectura: `imprenta` tiene un botón «Cambiar Foto»
          que no se portea porque acá NO hay dónde guardar una imagen — ni
          endpoint ni columna. Un botón que no puede funcionar es peor que no
          tenerlo (`TODO.md`). */}
      <div className="mb-6 flex items-center gap-4">
        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-100 font-bold text-2xl text-brand-700">
          {iniciales(me.nombre).charAt(0)}
        </span>
      </div>

      {editando ? (
        <form onSubmit={guardar} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <CampoConIcono
              id="nombre"
              icono={User}
              etiqueta="Nombre Completo"
              value={datos.nombre}
              onChange={(e) => setDatos((d) => ({ ...d, nombre: e.target.value }))}
              required
            />
            <CampoConIcono
              id="telefono"
              icono={Phone}
              type="tel"
              etiqueta="Teléfono"
              placeholder="+54 9 11 ..."
              value={datos.telefono}
              onChange={(e) => setDatos((d) => ({ ...d, telefono: e.target.value }))}
            />
          </div>

          <CampoConIcono
            id="direccion"
            icono={MapPin}
            etiqueta="Dirección"
            placeholder="Calle 123, Ciudad"
            value={datos.direccion}
            onChange={(e) => setDatos((d) => ({ ...d, direccion: e.target.value }))}
          />

          <div className="flex gap-3 pt-4">
            <Boton type="submit" disabled={guardando || datos.nombre.trim() === ""}>
              {guardando ? "Guardando..." : "Guardar Cambios"}
            </Boton>
            <Boton type="button" variante="secundario" onClick={cancelar} disabled={guardando}>
              Cancelar
            </Boton>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Dato icono={User} etiqueta="Nombre Completo" valor={me.nombre || "No especificado"} />
            <Dato icono={Phone} etiqueta="Teléfono" valor={me.telefono || "No especificado"} />
          </div>

          <Dato icono={MapPin} etiqueta="Dirección" valor={me.direccion || "No especificado"} />

          {/* Los roles NO se editan: los gobierna la consola con su contención,
              porque nadie se asciende a sí mismo (ADR 0019). */}
          <div className="grid grid-cols-1 gap-4 border-neutral-100 border-t pt-4 sm:grid-cols-2">
            <Dato
              icono={Shield}
              etiqueta={me.roles.length > 1 ? "Roles" : "Rol"}
              valor={me.roles.length > 0 ? me.roles.join(", ") : "Sin rol asignado"}
            />
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * Seguridad: el email y la contraseña, cada uno con su botón.
 *
 * Los dos formularios viven en modales y no desplegados: son operaciones que se
 * hacen una vez por año, y abiertas ocupaban dos tarjetas enteras de una
 * pantalla a la que se entra a mirar.
 */
function Seguridad({ emailActual }: { emailActual: string }) {
  const [modal, setModal] = useState<"email" | "contrasena" | null>(null);

  return (
    <Card className="p-6">
      <CabeceraDeTarjeta
        titulo="Seguridad"
        descripcion="Gestiona tu correo electrónico y contraseña"
      />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Mail aria-hidden="true" className="h-5 w-5 shrink-0 text-neutral-400" />
          <div className="min-w-0">
            <p className="font-medium text-neutral-900 text-sm">Correo Electrónico</p>
            <p className="truncate text-neutral-500 text-sm">{emailActual}</p>
          </div>
        </div>
        <Boton variante="secundario" onClick={() => setModal("email")}>
          Cambiar Email
        </Boton>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border-neutral-100 border-t pt-4">
        <div className="flex items-center gap-3">
          <Lock aria-hidden="true" className="h-5 w-5 shrink-0 text-neutral-400" />
          <div>
            <p className="font-medium text-neutral-900 text-sm">Contraseña</p>
            {/* `imprenta` dice acá «Última actualización: hace tiempo», un texto
                fijo que no mira ningún dato. No se portea una frase que miente:
                no guardamos cuándo se cambió. */}
            <p className="text-neutral-500 text-sm">
              Al cambiarla se cierran las demás sesiones abiertas
            </p>
          </div>
        </div>
        <Boton variante="secundario" onClick={() => setModal("contrasena")}>
          Cambiar Contraseña
        </Boton>
      </div>

      {modal === "email" ? (
        <ModalCambiarEmail emailActual={emailActual} alCerrar={() => setModal(null)} />
      ) : null}
      {modal === "contrasena" ? <ModalCambiarContrasena alCerrar={() => setModal(null)} /> : null}
    </Card>
  );
}

/**
 * Cambio de email — dos pasos, los dos por correo (ADR 0033).
 *
 * La pantalla dice explícitamente que el primer mail va a la dirección
 * **actual**, y no es un detalle de implementación filtrado a la UI: si alguien
 * pide el cambio y lee «te mandamos un mail», va a mirar la casilla nueva y no
 * va a entender por qué no llega nada. En `imprenta` el código sí llega a la
 * dirección nueva, y por eso allá no hace falta aclararlo.
 */
function ModalCambiarEmail({
  emailActual,
  alCerrar,
}: {
  emailActual: string;
  alCerrar: () => void;
}) {
  const [nuevo, setNuevo] = useState("");
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enviado, setEnviado] = useState(false);

  const limpio = nuevo.trim().toLowerCase();
  const valido = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(limpio) && limpio !== emailActual.toLowerCase();

  return (
    <Modal
      abierto
      onCerrar={alCerrar}
      titulo="Cambiar Email"
      descripcion="El cambio se confirma por correo, en dos pasos"
    >
      <div className="space-y-4">
        {error ? <Aviso>{error}</Aviso> : null}
        {enviado ? (
          <Aviso tono="ok">
            Te mandamos un mail a <strong>{emailActual}</strong> —tu dirección actual— para que
            apruebes el cambio. Recién cuando lo apruebes le vamos a pedir confirmación a la
            dirección nueva. Hasta entonces seguís entrando con la de siempre.
          </Aviso>
        ) : null}

        <Campo etiqueta="Correo actual" value={emailActual} readOnly disabled />
        <Campo
          etiqueta="Nuevo Correo"
          type="email"
          autoComplete="email"
          value={nuevo}
          onChange={(e) => setNuevo(e.target.value)}
        />

        <p className="text-neutral-500 text-sm">
          El primer mail va a tu dirección <strong>actual</strong>, no a la nueva: así nadie te
          cambia el email aunque tenga tu sesión abierta. Se puede pedir hasta 3 veces por hora.
        </p>

        <div className="flex justify-end gap-2">
          <Boton variante="secundario" onClick={alCerrar}>
            Cancelar
          </Boton>
          <Boton
            disabled={trabajando || !valido}
            onClick={async () => {
              setTrabajando(true);
              setError(null);
              setEnviado(false);
              try {
                const r = await authClient.changeEmail({
                  newEmail: limpio,
                  // Adónde vuelve el navegador después de cada uno de los dos
                  // clics. Al perfil, que es donde va a mirar si cambió.
                  callbackURL: `${window.location.origin}/perfil`,
                });
                if (r.error) throw new Error(r.error.message ?? "No se pudo pedir el cambio");
                setNuevo("");
                setEnviado(true);
              } catch (err) {
                setError(err instanceof Error ? err.message : "No se pudo pedir el cambio");
              } finally {
                setTrabajando(false);
              }
            }}
          >
            {trabajando ? "Enviando..." : "Cambiar Email"}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

function ModalCambiarContrasena({ alCerrar }: { alCerrar: () => void }) {
  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [repetida, setRepetida] = useState("");
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listo, setListo] = useState(false);

  const noCoinciden = repetida !== "" && nueva !== repetida;
  const completo = actual !== "" && nueva !== "" && nueva === repetida;

  return (
    <Modal
      abierto
      onCerrar={alCerrar}
      titulo="Cambiar Contraseña"
      descripcion="Necesitás la contraseña actual para poder cambiarla"
    >
      <div className="space-y-4">
        {error ? <Aviso>{error}</Aviso> : null}
        {listo ? (
          <Aviso tono="ok">Contraseña actualizada. Las demás sesiones se cerraron.</Aviso>
        ) : null}

        <Campo
          etiqueta="Contraseña Actual"
          type="password"
          autoComplete="current-password"
          value={actual}
          onChange={(e) => setActual(e.target.value)}
        />
        <Campo
          etiqueta="Nueva Contraseña"
          type="password"
          autoComplete="new-password"
          value={nueva}
          onChange={(e) => setNueva(e.target.value)}
        />
        <Campo
          etiqueta="Confirmar Nueva Contraseña"
          type="password"
          autoComplete="new-password"
          value={repetida}
          onChange={(e) => setRepetida(e.target.value)}
        />

        {noCoinciden ? <Aviso>Las dos contraseñas nuevas no coinciden.</Aviso> : null}

        <p className="text-neutral-500 text-sm">
          Al cambiarla se cierran las demás sesiones abiertas. La de este navegador sigue activa.
        </p>

        <div className="flex justify-end gap-2">
          <Boton variante="secundario" onClick={alCerrar}>
            Cancelar
          </Boton>
          <Boton
            disabled={trabajando || !completo}
            onClick={async () => {
              setTrabajando(true);
              setError(null);
              setListo(false);
              try {
                // `revokeOtherSessions`: si alguien cambia la contraseña es
                // porque sospecha que otro la tiene, y dejar esas sesiones vivas
                // vaciaría el gesto. Better Auth renueva la de este navegador.
                const r = await authClient.changePassword({
                  currentPassword: actual,
                  newPassword: nueva,
                  revokeOtherSessions: true,
                });
                if (r.error) throw new Error(r.error.message ?? "No se pudo cambiar");
                setActual("");
                setNueva("");
                setRepetida("");
                setListo(true);
              } catch (err) {
                setError(err instanceof Error ? err.message : "No se pudo cambiar la contraseña");
              } finally {
                setTrabajando(false);
              }
            }}
          >
            {trabajando ? "Cambiando..." : "Cambiar Contraseña"}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Plazo de bloqueo por inactividad (ADR 0034).
 *
 * El texto dice **«se bloquea la pantalla»** y no «expira la sesión», que es
 * como lo llama `imprenta`: la sesión del servidor no vence, lo que pasa es que
 * la interfaz se tapa. Prometer lo otro haría que alguien deje una máquina ajena
 * confiando en algo que no ocurre.
 */
function ConfiguracionDeSesion() {
  const [minutos, setMinutos] = usePreferenciaMinutos();

  return (
    <Card className="p-6">
      <CabeceraDeTarjeta
        titulo="Configuración de Sesión"
        descripcion="Personaliza el comportamiento de tu sesión"
      />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Clock aria-hidden="true" className="h-5 w-5 shrink-0 text-neutral-400" />
          <div>
            <p className="font-medium text-neutral-900 text-sm">Tiempo de inactividad</p>
            <p className="text-neutral-500 text-sm">
              La pantalla se bloquea a los {minutos} minutos sin actividad.
            </p>
          </div>
        </div>
        <select
          aria-label="Minutos de inactividad"
          value={minutos}
          onChange={(e) => setMinutos(Number(e.target.value))}
          className="h-9 rounded-md border border-neutral-300 bg-white px-3 text-neutral-900 text-sm"
        >
          {OPCIONES_MINUTOS.map((m) => (
            <option key={m} value={m}>
              {m} minutos
            </option>
          ))}
        </select>
      </div>

      <p className="mt-4 text-neutral-500 text-xs">
        Se destapa con tu contraseña, sin perder lo que estabas haciendo. Es una preferencia{" "}
        <strong>de este navegador</strong>: en otra máquina se configura aparte. No cierra la sesión
        —eso lo hace «Cerrar sesión»—, así que no reemplaza bloquear la computadora al irte.
      </p>
    </Card>
  );
}
